import { AlertTriangle, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DRAWER_PHRASING, type TenderPhrasing } from './tenderMath'

/** A payment type that is off on the review step. */
export interface OffTender {
  label: string
  over: number
  short: number
  phrasing?: TenderPhrasing
}

/**
 * The one line most people read on the review step. It must describe every
 * tender, never cash alone: a close once announced "Rs 15,997 short" while
 * card was Rs 17,589 short, the bigger hole invisible in the headline.
 *
 * When several are off it reports the COUNT, not a sum. Cash, card and online
 * are separate pots (drawer, machine batch, bank), so netting one's surplus
 * against another's shortfall would state a number that is true of nothing.
 */
export function ReviewStatus({
  offTenders,
  totalTenders,
  needsNote,
  loading,
  formatCurrency,
}: {
  offTenders: OffTender[]
  totalTenders: number
  needsNote: boolean
  loading?: boolean
  formatCurrency: (n: number) => string
}) {
  if (loading) {
    return (
      <div
        className="flex animate-pulse items-start gap-3 rounded-xl bg-muted/40 p-4"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="h-5 w-5 shrink-0 rounded-full bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-40 rounded bg-muted" />
          <div className="h-3.5 w-64 rounded bg-muted/70" />
        </div>
      </div>
    )
  }

  if (offTenders.length === 0) {
    return (
      <Banner tone="ok" headline="All totals match" detail="Nothing to explain. Finish when you're ready." />
    )
  }

  const { headline, detail } = describe(offTenders, totalTenders, needsNote, formatCurrency)
  return <Banner tone={needsNote ? 'bad' : 'warn'} headline={headline} detail={detail} />
}

function describe(
  off: OffTender[],
  totalTenders: number,
  needsNote: boolean,
  formatCurrency: (n: number) => string,
): { headline: string; detail: string } {
  const detail = needsNote
    ? 'Recount if you can. Otherwise write what happened in the note below.'
    : 'A small difference. Add a note below if you know why.'

  if (off.length === 1) {
    const t = off[0]
    const p = t.phrasing ?? DRAWER_PHRASING
    const amount = t.over > 0 ? t.over : t.short
    return {
      headline: `${t.label} is ${formatCurrency(amount)} ${t.over > 0 ? p.over : p.short}`,
      detail,
    }
  }

  const allShort = off.every((t) => t.short > 0)
  const allOver = off.every((t) => t.over > 0)
  const direction = allShort ? 'short' : allOver ? 'over' : null
  const count = off.length === totalTenders ? `All ${numberWord(off.length)}` : String(off.length)
  const headline = direction
    ? `${count} totals are ${direction}`
    : `${count} totals don't match`
  const which = off.map((t) => t.label).join(', ')
  return { headline, detail: `${which}. ${detail}` }
}

function numberWord(n: number): string {
  return ['zero', 'one', 'two', 'three', 'four', 'five'][n] ?? String(n)
}

function Banner({
  tone,
  headline,
  detail,
}: {
  tone: 'ok' | 'warn' | 'bad'
  headline: string
  detail: string
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl p-4',
        tone === 'ok' && 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100',
        tone === 'warn' && 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
        tone === 'bad' && 'bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100',
      )}
    >
      {tone === 'ok' ? (
        <Check className="mt-0.5 h-5 w-5 shrink-0" />
      ) : (
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      )}
      <div>
        <div className="text-lg font-semibold leading-tight">{headline}</div>
        <div className="mt-1 text-sm opacity-90">{detail}</div>
      </div>
    </div>
  )
}
